import {
  PNGS,
  SVGS,
  WabaCreationSuccess,
  WabaLaunch,
  WabaPreRequirements,
  WabaSignup,
  WabaLoader,
  WabaGenerateLink,
  WabaCopyShortLink,
  WabaFailure,
  SkipWABASetupScreen,
  DeployAgentBotAdvantages,
} from '@zonofi/gandalf-components';
import { useWabaSetupScreen } from '@zonofi/gandalf-utilities';
import React, { useEffect, useState } from 'react';
import './waba-setup-screen.scss';
import { LinearProgress } from '@mui/material';

// Add Facebook SDK type declaration
declare global {
  interface Window {
    FB?: {
      login: (
        callback: (response: {
          authResponse?: { accessToken: string };
          status?: string;
        }) => void,
        options: Record<string, unknown>
      ) => void;
      getLoginStatus: (
        callback: (response: {
          status?: string;
          authResponse?: unknown;
        }) => void
      ) => void;
      logout: (callback: () => void) => void;
      init: (options: Record<string, unknown>) => void;
    };
    fbq?: (
      command: string,
      event: string,
      params?: Record<string, unknown>
    ) => void;
  }
}

export const WabaSetupScreen: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if user is on mobile
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || '';
      const mobileRegex =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent));
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Add code to reset Facebook login state
  useEffect(() => {
    // Reset Facebook SDK login state when component loads
    const resetFacebookState = () => {
      if (window.FB) {
        // Wait until FB SDK is fully loaded
        window.FB.getLoginStatus(function (response) {
          // Force a status check which refreshes the session
          if (response && response.status === 'connected') {
            // Logout to clear the auth state
            window.FB.logout(function () {
              console.log('Facebook login state has been reset');
            });
          }
        });
      }
    };

    // Check if FB SDK is loaded, if not, check again in 1s
    const checkAndResetFB = () => {
      if (window.FB) {
        resetFacebookState();
      } else {
        setTimeout(checkAndResetFB, 1000);
      }
    };

    checkAndResetFB();
  }, []);

  const {
    calculateCompletionPercentage,
    generateSignupLink,
    goToWabaRequirements,
    goToWabaSignup,
    isWabaSigningUp,
    launchWhatsAppSignup,
    mobileNumber,
    navigateToHome,
    stepFromPath,
    updateMobileNumber,
    wabaData,
    goToWabaGenerateLink,
    goToWabaCopyShortLink,
    navigateToSkipLauchPage,
  } = useWabaSetupScreen();

  const renderStep = () => {
    switch (stepFromPath) {
      case 'requirements':
        return (
          <WabaPreRequirements
            onBackClick={navigateToHome}
            onSkipClick={navigateToSkipLauchPage}
            onContinueClick={() =>
              isMobile ? goToWabaGenerateLink() : goToWabaSignup()
            }
          />
        );
      case 'generateLink':
        return (
          <WabaGenerateLink
            onBackClick={goToWabaRequirements}
            onSkipClick={navigateToSkipLauchPage}
            onContinueClick={goToWabaCopyShortLink}
          />
        );
      case 'copyShortLink':
        return (
          <WabaCopyShortLink
            onBackClick={goToWabaGenerateLink}
            onSkipClick={navigateToSkipLauchPage}
          />
        );
      case 'signUp':
        return (
          <WabaSignup
            isLoading={isWabaSigningUp}
            mobileNumber={mobileNumber}
            onBackClick={goToWabaRequirements}
            onContinueClick={generateSignupLink}
            updateMobileNumber={updateMobileNumber}
            wabaData={wabaData}
          />
        );
      case 'launch':
        return (
          <WabaLaunch
            displayName={wabaData?.displayName}
            mobileNumber={mobileNumber}
            onBackClick={goToWabaSignup}
            onContinueClick={() => {
              // Reset Facebook login state before launching the signup
              if (window.FB) {
                window.FB.getLoginStatus(function (response) {
                  if (response && response.status === 'connected') {
                    window.FB.logout(function () {
                      console.log('Reset Facebook state before new login');
                      launchWhatsAppSignup();
                    });
                  } else {
                    launchWhatsAppSignup();
                  }
                });
              } else {
                launchWhatsAppSignup();
              }
            }}
          />
        );
      case 'success':
        return (
          <WabaCreationSuccess userData={wabaData} goToHome={navigateToHome} />
        );
      case 'loader':
        return <WabaLoader />;
      case 'facebook-redirect-handler':
        return <WabaLoader />;
      case 'failure':
        return <WabaFailure onBackClick={goToWabaRequirements} />;
      case 'skipLaunch':
        return <SkipWABASetupScreen />;
      case 'botAdvantages':
        return (
          <DeployAgentBotAdvantages
            onBackClick={navigateToHome}
            onSkipClick={navigateToSkipLauchPage}
            onContinueClick={() => navigateToSkipLauchPage()}
          />
        );
      default:
        return null;
    }
  };
  return (
    <div className="waba-setup-screen">
      <div className="waba-setup-screen-header">
        <div className="waba-setup-screen-header-context">
          <img
            src={PNGS.Whatsapp}
            alt=""
            className="waba-setup-screen-header-image"
          />
          <div className="waba-setup-screen-header-content">
            <span className="waba-setup-screen-header-content-text primary-font-semibold">
              STEP 3
            </span>
            <span className="waba-setup-screen-header-content-heading primary-font-bold">
              Deploy Agent On Your WhatsApp
            </span>
          </div>
          <div
            className="waba-setup-screen-header-home"
            onClick={() => {
              navigateToHome();
            }}
          >
            <img
              src={SVGS.HomeIcon}
              alt=""
              className="waba-setup-screen-header-home-icon"
            />
          </div>
        </div>
        <div className="waba-setup-screen-header-progress">
          <LinearProgress
            variant="determinate"
            value={calculateCompletionPercentage()}
            sx={{
              width: '100%',
              height: '6px',
              zIndex: 5,
              backgroundColor: '#D9D9D9',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#2ABB7F',
              },
            }}
          />
        </div>
      </div>

      <div className="waba-setup-screen-body">{renderStep()}</div>
    </div>
  );
};
